import React from "react"

const palette = {
  ink: "#0f172a",
  inkSoft: "#334155",
  textMuted: "#64748b",
  line: "#e2e8f0",
  lineSoft: "#eef2f7",
  surface: "#ffffff",
  surfaceAlt: "#f8fafc",
  shadow: "0 10px 30px rgba(15,23,42,0.06)",
  shadowSoft: "0 6px 18px rgba(15,23,42,0.04)",
  blue: {
    bg: "#eff6ff",
    line: "#bfdbfe",
    text: "#1d4ed8",
  },
  green: {
    bg: "#f0fdf4",
    line: "#bbf7d0",
    text: "#166534",
  },
  amber: {
    bg: "#fffbeb",
    line: "#fde68a",
    text: "#92400e",
  },
  red: {
    bg: "#fef2f2",
    line: "#fecaca",
    text: "#991b1b",
  },
  slate: {
    bg: "#f8fafc",
    line: "#cbd5e1",
    text: "#334155",
  },
}

export const planInputStyle = {
  width: "100%",
  minHeight: 44,
  padding: "11px 13px",
  borderRadius: 12,
  border: `1px solid ${palette.line}`,
  outline: "none",
  background: palette.surface,
  color: palette.ink,
  fontSize: 14,
  fontWeight: 500,
  boxSizing: "border-box",
}

export const planTextareaStyle = {
  ...planInputStyle,
  minHeight: 108,
  resize: "vertical",
  lineHeight: 1.45,
}

export const planButtonStyle = {
  minHeight: 40,
  padding: "9px 13px",
  borderRadius: 12,
  border: `1px solid ${palette.line}`,
  fontWeight: 800,
  fontSize: 13,
  cursor: "pointer",
  background: palette.surface,
  color: palette.ink,
  boxShadow: palette.shadowSoft,
}

export const planLabelStyle = {
  display: "block",
  fontSize: 12,
  color: palette.textMuted,
  marginBottom: 6,
  fontWeight: 800,
  letterSpacing: 0.2,
}

export function SectionCard({ title, children, right }) {
  return (
    <div
      style={{
        border: `1px solid ${palette.line}`,
        background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
        borderRadius: 18,
        padding: 18,
        boxShadow: palette.shadow,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: 14,
        }}
      >
        <div
          style={{
            fontSize: 17,
            fontWeight: 900,
            color: palette.ink,
            letterSpacing: "-0.02em",
          }}
        >
          {title}
        </div>
        {right}
      </div>
      {children}
    </div>
  )
}

export function MetricCard({ title, value, helper, tone = "neutral" }) {
  const tones = {
    neutral: {
      bg: palette.surface,
      border: palette.line,
      value: palette.ink,
    },
    info: {
      bg: palette.blue.bg,
      border: palette.blue.line,
      value: palette.blue.text,
    },
    success: {
      bg: palette.green.bg,
      border: palette.green.line,
      value: palette.green.text,
    },
    warning: {
      bg: palette.amber.bg,
      border: palette.amber.line,
      value: palette.amber.text,
    },
    danger: {
      bg: palette.red.bg,
      border: palette.red.line,
      value: palette.red.text,
    },
  }

  const visual = tones[tone] || tones.neutral

  return (
    <div
      style={{
        border: `1px solid ${visual.border}`,
        borderRadius: 16,
        padding: 14,
        background: visual.bg,
        boxShadow: palette.shadowSoft,
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          color: palette.textMuted,
          textTransform: "uppercase",
          fontWeight: 900,
          letterSpacing: 0.45,
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: 8,
          fontSize: 24,
          fontWeight: 900,
          color: visual.value,
          lineHeight: 1.05,
          letterSpacing: "-0.03em",
        }}
      >
        {value}
      </div>

      {helper ? (
        <div
          style={{
            marginTop: 6,
            fontSize: 12.5,
            color: palette.textMuted,
            lineHeight: 1.35,
          }}
        >
          {helper}
        </div>
      ) : null}
    </div>
  )
}

export function StatusBadge({ status }) {
  const upper = String(status || "").toUpperCase()

  const map = {
    ACTIVE: { bg: palette.blue.bg, border: palette.blue.line, color: palette.blue.text, label: "Activo" },
    COMPLETED: { bg: palette.green.bg, border: palette.green.line, color: palette.green.text, label: "Completado" },
    CANCELLED: { bg: palette.red.bg, border: palette.red.line, color: palette.red.text, label: "Cancelado" },
    PENDING: { bg: palette.amber.bg, border: palette.amber.line, color: palette.amber.text, label: "Pendiente" },
    SCHEDULED: { bg: palette.blue.bg, border: palette.blue.line, color: palette.blue.text, label: "Agendado" },
    IN_PROGRESS: { bg: "#f5f3ff", border: "#ddd6fe", color: "#6d28d9", label: "En proceso" },
    DONE: { bg: palette.green.bg, border: palette.green.line, color: palette.green.text, label: "Realizado" },
  }

  const visual = map[upper] || {
    bg: palette.slate.bg,
    border: palette.slate.line,
    color: palette.slate.text,
    label: upper || "-",
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 30,
        padding: "5px 10px",
        borderRadius: 999,
        background: visual.bg,
        border: `1px solid ${visual.border}`,
        color: visual.color,
        fontSize: 12,
        fontWeight: 900,
        boxSizing: "border-box",
      }}
    >
      {visual.label}
    </span>
  )
}

export function QuickChip({ active, label, onClick, tone = "neutral" }) {
  const tones = {
    neutral: {
      bg: active ? palette.ink : palette.surface,
      color: active ? "#ffffff" : palette.inkSoft,
      border: active ? palette.ink : palette.line,
    },
    info: {
      bg: active ? palette.blue.text : palette.blue.bg,
      color: active ? "#ffffff" : palette.blue.text,
      border: palette.blue.line,
    },
    success: {
      bg: active ? palette.green.text : palette.green.bg,
      color: active ? "#ffffff" : palette.green.text,
      border: palette.green.line,
    },
    warning: {
      bg: active ? palette.amber.text : palette.amber.bg,
      color: active ? "#ffffff" : palette.amber.text,
      border: palette.amber.line,
    },
    danger: {
      bg: active ? palette.red.text : palette.red.bg,
      color: active ? "#ffffff" : palette.red.text,
      border: palette.red.line,
    },
  }

  const visual = tones[tone] || tones.neutral

  return (
    <button
      onClick={onClick}
      style={{
        minHeight: 36,
        padding: "8px 12px",
        borderRadius: 999,
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        color: visual.color,
        cursor: "pointer",
        fontWeight: 900,
        fontSize: 12,
        boxShadow: active ? "0 8px 18px rgba(15,23,42,0.08)" : "none",
      }}
    >
      {label}
    </button>
  )
}

export function ExecutiveSimpleTable({ headers, rows, emptyLabel }) {
  if (!rows.length) {
    return (
      <div
        style={{
          color: palette.textMuted,
          border: `1px dashed ${palette.line}`,
          borderRadius: 16,
          padding: 18,
          background: palette.surfaceAlt,
        }}
      >
        {emptyLabel}
      </div>
    )
  }

  return (
    <div
      style={{
        overflowX: "auto",
        border: `1px solid ${palette.line}`,
        borderRadius: 16,
        background: palette.surface,
        boxShadow: palette.shadowSoft,
      }}
    >
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th
                key={`${header}-${index}`}
                style={{
                  textAlign: "left",
                  fontSize: 11.5,
                  color: palette.textMuted,
                  borderBottom: `1px solid ${palette.line}`,
                  padding: "12px 10px",
                  whiteSpace: "nowrap",
                  background: "#f8fbff",
                  textTransform: "uppercase",
                  letterSpacing: 0.35,
                  fontWeight: 900,
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, idx) => {
            const rowTone = row.rowTone || "normal"

            const rowBackground =
              rowTone === "danger"
                ? "#fff9f9"
                : rowTone === "warning"
                  ? "#fffdf7"
                  : rowTone === "success"
                    ? "#f8fff9"
                    : "#ffffff"

            const rowBorder =
              rowTone === "danger"
                ? palette.red.line
                : rowTone === "warning"
                  ? palette.amber.line
                  : rowTone === "success"
                    ? palette.green.line
                    : palette.lineSoft

            return (
              <tr key={idx}>
                {row.cells.map((cell, cellIdx) => (
                  <td
                    key={cellIdx}
                    style={{
                      padding: "14px 10px",
                      borderBottom: `1px solid ${rowBorder}`,
                      color: palette.inkSoft,
                      fontSize: 14,
                      verticalAlign: "top",
                      background: rowBackground,
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function TreatmentDraftCard({ item, index, onRemove, formatMoney }) {
  return (
    <div
      style={{
        border: `1px solid ${palette.line}`,
        background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        borderRadius: 16,
        padding: 14,
        display: "grid",
        gap: 10,
        boxShadow: palette.shadowSoft,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "start",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontWeight: 900,
              color: palette.ink,
              lineHeight: 1.25,
            }}
          >
            {index + 1}. {item.treatmentName || "Tratamiento"}
          </div>

          <div
            style={{
              fontSize: 13,
              color: palette.textMuted,
              marginTop: 6,
              lineHeight: 1.4,
            }}
          >
            {item.tooth ? `Pieza ${item.tooth}` : "Sin pieza"}
            {item.surface ? ` · Superficie ${item.surface}` : ""}
            {item.condition ? ` · Condición ${item.condition}` : ""}
          </div>

          {item.notes ? (
            <div
              style={{
                fontSize: 13,
                color: palette.inkSoft,
                marginTop: 8,
                lineHeight: 1.45,
              }}
            >
              {item.notes}
            </div>
          ) : null}
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div
            style={{
              padding: "7px 10px",
              borderRadius: 999,
              background: palette.surface,
              border: `1px solid ${palette.line}`,
              fontSize: 12,
              fontWeight: 900,
              color: palette.ink,
            }}
          >
            {formatMoney(item.price || 0)}
          </div>

          <button
            type="button"
            onClick={onRemove}
            style={{
              ...planButtonStyle,
              background: palette.red.bg,
              border: `1px solid ${palette.red.line}`,
              color: palette.red.text,
            }}
          >
            Quitar
          </button>
        </div>
      </div>
    </div>
  )
}